/**
 * Runtime environment configuration.
 *
 * `window.env` is populated at runtime by `public/env.js` (loaded as a static
 * script tag in `index.html`), allowing the same build artefact to be deployed
 * to multiple environments without rebuilding. `import.meta.env` provides the
 * Vite-compile-time variables. The DEV branch backports `VITE_*` keys to the
 * legacy `REACT_APP_*` shape that pre-Vite consumers still reference.
 *
 * NOTE: Vite's `import.meta.env` includes booleans (`DEV`, `PROD`, `SSR`).
 * These are stringified at the boundary so `AppEnv` stays a string-only
 * record — every consumer (e.g. `okta.config.ts`) treats values as strings.
 */

export type AppEnv = Record<string, string | undefined> & {
  REACT_APP_ENV?: string;
  PUBLIC_URL?: string;
  NODE_ENV?: string;
};

declare global {
  interface Window {
    env?: Record<string, string | undefined>;
  }
}

const stringifyEnv = (
  source: Record<string, unknown>
): Record<string, string | undefined> => {
  const out: Record<string, string | undefined> = {};

  Object.keys(source).forEach((key) => {
    const value = source[key];

    if (value === undefined) {
      out[key] = undefined;
    } else {
      out[key] = String(value);
    }
  });

  return out;
};

const viteEnv = stringifyEnv(
  (import.meta.env || {}) as Record<string, unknown>
);

export const getEnv = (): AppEnv => {
  const env: AppEnv = {
    ...viteEnv,
    ...((typeof window !== 'undefined' ? window.env : undefined) ?? {}),
  };

  if (import.meta.env.DEV) {
    const convertedEnv: Record<string, string | undefined> = {};

    Object.keys(env).forEach((key) => {
      if (key.startsWith('VITE_')) {
        const reactAppKey = key.replace('VITE_', 'REACT_APP_');

        convertedEnv[reactAppKey] = env[key];
      }
    });

    return {
      ...env,
      ...convertedEnv,
    };
  }

  return env;
};

export const env = getEnv();
